import { useState, useCallback, useEffect } from "react";
import { useTheme } from "../components/ThemeProvider";
import MarcoNasExplorer from "../components/MarcoNasExplorer";
import api from "../services/api";
import { formatApiError } from "../utils/helpers";
import { dialogs } from "../context/DialogContext";
import { verificarCarpetasEditar } from "../utils/marcoRetoqueVerify";
import usePermiso from "../hooks/usePermiso";

const POLL_RETOQUE_MS = 3000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function resolverCarpetasEditar(captaciones) {
  const verData = await verificarCarpetasEditar(captaciones);
  if (!verData?.ok && !verData?.captaciones?.length) {
    throw new Error("No se pudo verificar las carpetas Editar.");
  }

  const carpetasEditar = {};
  const omitidas = [];

  for (const v of verData.captaciones || []) {
    if (v.estado === "ok") continue;

    if (v.estado === "error" || v.estado === "falta") {
      await dialogs.alert(
        `${v.mensaje}\n\nMarco no editará «${v.captacion}». Renombra la carpeta a «Editar» en el NAS y vuelve a ejecutar.`,
      );
      omitidas.push({ captacion: v.captacion, motivo: v.mensaje });
      continue;
    }

    if (v.estado === "sugerencia" && v.sugerencias?.length) {
      const sug = v.sugerencias[0];
      const otras = v.sugerencias.slice(1, 3).map((s) => s.nombre);
      let msg = `${v.mensaje}\n\n¿Quieres que Marco use la carpeta «${sug.nombre}»`;
      if (sug.motivo === "solo_difiere_mayusculas") {
        msg += " (mismo nombre, distinto uso de mayúsculas)";
      } else {
        msg += " (nombre muy parecido a «Editar»)";
      }
      msg += "?";
      if (otras.length) msg += `\n\nOtras parecidas: «${otras.join("», «")}».`;
      msg += "\n\nSi respondes No, Marco no editará esta captación.";

      const usar = await dialogs.confirm(msg);
      if (usar) {
        carpetasEditar[v.captacion] = sug.nombre;
      } else {
        omitidas.push({
          captacion: v.captacion,
          motivo: `No se ejecutó: se rechazó usar «${sug.nombre}» en lugar de «Editar»`,
        });
      }
    }
  }

  const aProcesar = captaciones.filter((c) => !omitidas.some((o) => o.captacion === c));
  return { aProcesar, omitidas, carpetasEditar };
}

const accent = {
  from: "#7c3aed",
  to:   "#a855f7",
  bg:   "rgba(124,58,237,0.08)",
  border: "rgba(124,58,237,0.25)",
  text: "#a78bfa",
};

export default function Marco() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const veExplorer = puede("cfg_marco_explorer", "ver");
  const veProcesar = puede("cfg_marco_procesar", "ver");
  const modProcesar = puede("cfg_marco_procesar", "modificar");

  const [captacionesSeleccionadas, setCaptacionesSeleccionadas] = useState([]);
  const [procesandoRetoque, setProcesandoRetoque] = useState(false);
  const [jobIdRetoque, setJobIdRetoque] = useState(null);
  const [progresoRetoque, setProgresoRetoque] = useState(null);
  const [errorRetoque, setErrorRetoque] = useState("");
  const [resultadoRetoque, setResultadoRetoque] = useState(null);
  const [colaAuto, setColaAuto] = useState([]);
  const [presetsMarco, setPresetsMarco] = useState(null);
  const [referencia, setReferencia] = useState("");
  const [titulo, setTitulo]         = useState("");
  const [carpetas, setCarpetas]     = useState([]);
  const [copiado, setCopiado]       = useState(false);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [generando, setGenerando]   = useState(false);
  const [error, setError]           = useState("");
  const [ultimoExito, setUltimoExito] = useState(null);
  const [referenciaVideoOk, setReferenciaVideoOk] = useState(true);

  const baseUrl = import.meta.env.BASE_URL || "/";
  const refVideoSrc = baseUrl.endsWith("/") ? `${baseUrl}video-referencia-marco.mp4` : `${baseUrl}/video-referencia-marco.mp4`;

  const onReferenciaVideoError = useCallback(() => {
    setReferenciaVideoOk(false);
  }, []);

  useEffect(() => {
    let cancel = false;
    const cargarPresets = async () => {
      try {
        const res = await api.get("/multimedia/retoque/presets");
        if (!cancel && res.data) setPresetsMarco(res.data);
      } catch {
        if (!cancel) setPresetsMarco({ ok: false, error: "No se pudo comprobar presets Autoenhance" });
      }
    };
    cargarPresets();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (procesandoRetoque) return undefined;

    let cancel = false;
    const refrescarCola = async () => {
      try {
        const res = await api.get("/multimedia/retoque/cola?limit=10");
        if (cancel || !res.data?.ok) return;
        const activos = (res.data.items || []).filter(
          (i) => i.status === "pending" || i.status === "running",
        );
        setColaAuto(activos);
      } catch {
        /* cola opcional */
      }
    };

    refrescarCola();
    const t = setInterval(refrescarCola, 30_000);
    return () => {
      cancel = true;
      clearInterval(t);
    };
  }, [procesandoRetoque]);

  const ejecutarRetoque = async () => {
    if (!modProcesar) return;
    setErrorRetoque("");
    setResultadoRetoque(null);
    setJobIdRetoque(null);
    if (!captacionesSeleccionadas.length) {
      setErrorRetoque("Marca al menos una captación en la lista.");
      return;
    }

    setProcesandoRetoque(true);

    try {
      const { aProcesar, omitidas, carpetasEditar } = await resolverCarpetasEditar(captacionesSeleccionadas);

      if (!aProcesar.length) {
        setErrorRetoque("No se ha ejecutado la edición en ninguna captación.");
        setResultadoRetoque({ omitidas, resultados: [], fotosOk: 0, fallos: [], fotosFallidas: [] });
        return;
      }

      const start = await api.post("/multimedia/retoque/ejecutar", {
        captaciones: aProcesar,
        carpetasEditar,
      });
      const data = start.data;
      if (!data.ok || !data.jobId) {
        throw new Error(formatApiError(data, "No se pudo iniciar el trabajo."));
      }

      const id = data.jobId;
      setJobIdRetoque(id);

      let job = null;
      while (true) {
        const res = await api.get(`/multimedia/retoque/jobs/${encodeURIComponent(id)}`);
        job = res.data;
        if (!job?.ok) {
          throw new Error(formatApiError(job, "No se pudo consultar el progreso."));
        }

        setProgresoRetoque({
          status: job.status,
          fase: job.fase,
          captacionActual: job.captacionActual,
          captacionIndex: (job.captacionIndex ?? 0) + 1,
          captacionesTotal: job.captaciones?.length || captacionesSeleccionadas.length,
          fotoActual: job.fotoActual,
          fotoIndex: job.fotoIndex != null ? job.fotoIndex + 1 : null,
          fotosTotal: job.fotosTotalEnCaptacion || null,
          videoClipActual: job.videoClipActual,
          videoClipIndex: job.videoClipIndex,
          videoClipsTotal: job.videoClipsTotal,
          resumen: job.resumen,
        });

        if (job.status === "done" || job.status === "done_with_errors" || job.status === "error") {
          break;
        }
        await sleep(POLL_RETOQUE_MS);
      }

      const resultados = job.resultados || [];
      const fotosOk = job.resumen?.fotosOk ?? resultados.reduce((n, r) => n + (r.procesadas || 0), 0);
      const fallos = resultados.filter((r) => !r.ok || r.errores > 0);
      const fotosFallidas = resultados.flatMap((cap) =>
        (cap.resultados || [])
          .filter((f) => !f.ok)
          .map((f) => ({ captacion: cap.captacion, archivo: f.archivo, error: f.error })),
      );

      setResultadoRetoque({
        jobId: id,
        status: job.status,
        resultados,
        fotosOk,
        fallos,
        fotosFallidas,
        resumen: job.resumen,
        omitidas,
        carpetasEditar,
      });

      if (job.status === "error") {
        setErrorRetoque(job.error || "El trabajo terminó con un error fatal.");
      } else if (omitidas.length) {
        setErrorRetoque(
          `${omitidas.length} captación(es) no se editaron (carpeta Editar). ${fallos.length || fotosFallidas.length ? "Además hay errores en el detalle." : ""}`.trim(),
        );
      } else if (fallos.length || fotosFallidas.length) {
        setErrorRetoque(
          `${fotosFallidas.length || fallos.length} foto(s) o captación(es) con errores. Revisa el detalle abajo.`,
        );
      }
    } catch (e) {
      setErrorRetoque(formatApiError(e, "Error al procesar fotos."));
    } finally {
      setProcesandoRetoque(false);
      setProgresoRetoque(null);
    }
  };

  const buscarCarpetas = async () => {
    setError("");
    setCarpetas([]);
    setUltimoExito(null);
    const ref = referencia.trim();
    if (!ref) {
      setError("Indica la referencia (ej. 3494-C).");
      return;
    }
    setCargandoLista(true);
    try {
      const res = await api.get(`/multimedia/video/carpetas/${encodeURIComponent(ref)}`);
      const data = res.data;
      if (!data.ok) {
        setError(formatApiError(data, "No se pudo consultar el NAS."));
        return;
      }
      setCarpetas(data.carpetas || []);
      if (!data.carpetas?.length) {
        setError(`No hay carpeta que empiece por «${data.prefijoBuscado}» en el NAS.`);
      }
    } catch (e) {
      setError(formatApiError(e, "Error de red al consultar carpetas."));
    } finally {
      setCargandoLista(false);
    }
  };

  const generarVideo = async () => {
    setError("");
    setUltimoExito(null);
    const ref = referencia.trim();
    if (!ref) {
      setError("Indica la referencia.");
      return;
    }
    setGenerando(true);
    try {
      const body = { referencia: ref };
      if (titulo.trim()) body.titulo = titulo.trim();

      const res = await api.post("/multimedia/video/desde-nas", body);
      const data = res.data;
      if (!data.ok) {
        throw new Error(formatApiError(data, "Error al generar el vídeo."));
      }

      const adv = Array.isArray(data.advertencias) ? data.advertencias.filter(Boolean).join(" · ") : "";

      setUltimoExito({
        urlLanding: data.urlLanding || "",
        urlVideo: data.urlVideo || "",
        rutaNas: data.rutaNas || null,
        carpeta: data.carpetaNas || "",
        fotos: data.fotosUsadas != null ? String(data.fotosUsadas) : "?",
        advertencias: adv,
      });
    } catch (e) {
      setError(formatApiError(e, "Error al generar el vídeo."));
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: c.bgPage || c.bg, padding: "32px 24px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <div style={{ marginBottom: 22 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: c.textPrimary, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg,${accent.from},${accent.to})`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
              boxShadow: "0 4px 12px rgba(124,58,237,0.35)",
            }}>🤖</span>
            Marco
          </h1>
          <p style={{ margin: "8px 0 0 0", fontSize: 14, color: c.textMuted, fontWeight: 500 }}>
            Edición de fotos y vídeos desde el NAS
          </p>
        </div>

        {/* ── Retoque fotos: explorador NAS ── */}
        {veProcesar && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: c.textPrimary }}>
              Fotos para Retoque
            </h2>
            <p style={{ margin: "6px 0 0 0", fontSize: 13, color: c.textMuted, lineHeight: 1.5 }}>
              Las fotos en <strong style={{ color: c.textPrimary }}>Editar</strong> solo se procesan cuando la secretaria marca la captación abajo y pulsa <strong style={{ color: c.textPrimary }}>Ejecutar Marco</strong>.
              Flujo por foto: clasificación interior/exterior (CLIP) → recorte 16:9 → Autoenhance con preset <strong style={{ color: c.textPrimary }}>Interior</strong> o <strong style={{ color: c.textPrimary }}>exterior</strong> → <strong style={{ color: c.textPrimary }}>Web</strong> (≤1 MB).
              Marco sigue revisando el NAS cada pocos minutos para continuar automáticamente vídeo horizontal/vertical y archivar cuando las fotos ya están en <strong style={{ color: c.textPrimary }}>Web</strong>.
              Tras las fotos genera <strong style={{ color: c.textPrimary }}>Web/Video horizontal.mp4</strong> (slideshow Ken Burns).
              Si hay clips <strong style={{ color: c.textPrimary }}>VID_*.mp4</strong>, también <strong style={{ color: c.textPrimary }}>Web/Video vertical.mp4</strong> con música ambiental.
              Al terminar sin errores, borra <strong style={{ color: c.textPrimary }}>Editar</strong> y mueve la captación a <strong style={{ color: c.textPrimary }}>Referencias</strong>.
              Si el nombre de carpeta no coincide, Marco avisará antes de procesar manualmente.
            </p>
          </div>

          {presetsMarco && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: presetsMarco.ok
                  ? (isDark ? "rgba(34, 197, 94, 0.1)" : "rgba(34, 197, 94, 0.08)")
                  : (isDark ? "rgba(239, 68, 68, 0.12)" : "rgba(239, 68, 68, 0.08)"),
                border: `1px solid ${presetsMarco.ok ? "rgba(34, 197, 94, 0.35)" : "rgba(239, 68, 68, 0.35)"}`,
                fontSize: 13,
                color: c.textPrimary,
                lineHeight: 1.45,
              }}
            >
              {presetsMarco.ok ? (
                <>
                  <strong>Autoenhance listo</strong>
                  {presetsMarco.classifyEnabled !== false && (
                    <> — preset <strong>Interior</strong> y <strong>exterior</strong> encontrados en la cuenta.</>
                  )}
                </>
              ) : (
                <>
                  <strong>Autoenhance no listo</strong>
                  {presetsMarco.error ? `: ${presetsMarco.error}` : ""}
                  {" "}Marco no editará fotos hasta que existan ambos presets.
                </>
              )}
            </div>
          )}

          {colaAuto.length > 0 && !procesandoRetoque && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: isDark ? "rgba(56, 189, 248, 0.12)" : "rgba(14, 165, 233, 0.1)",
                border: `1px solid ${isDark ? "rgba(56, 189, 248, 0.35)" : "rgba(14, 165, 233, 0.35)"}`,
                fontSize: 13,
                color: c.textPrimary,
                lineHeight: 1.45,
              }}
            >
              <strong>Cola automática activa</strong> — {colaAuto.length} captación{colaAuto.length !== 1 ? "es" : ""} en proceso o pendiente
              {colaAuto[0]?.captacion ? `: «${colaAuto[0].captacion}»${colaAuto.length > 1 ? ` (+${colaAuto.length - 1} más)` : ""}` : ""}.
              {colaAuto.some((i) => i.status === "running") && colaAuto.find((i) => i.status === "running")?.fase
                ? ` Fase: ${colaAuto.find((i) => i.status === "running").fase}.`
                : ""}
            </div>
          )}

          {veExplorer && (
            <MarcoNasExplorer onSelectionChange={setCaptacionesSeleccionadas} />
          )}

          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={ejecutarRetoque}
              disabled={procesandoRetoque || captacionesSeleccionadas.length === 0 || presetsMarco?.ok === false || !modProcesar}
              title={!modProcesar ? "No tienes permiso para procesar captaciones" : undefined}
              style={{
                width: "100%",
                padding: "14px 20px",
                borderRadius: 10,
                border: "none",
                background: procesandoRetoque || captacionesSeleccionadas.length === 0 || presetsMarco?.ok === false || !modProcesar
                  ? (isDark ? "#334155" : "#cbd5e1")
                  : `linear-gradient(135deg,${accent.from},${accent.to})`,
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: procesandoRetoque || captacionesSeleccionadas.length === 0 || presetsMarco?.ok === false || !modProcesar ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                boxShadow: procesandoRetoque || captacionesSeleccionadas.length === 0 || presetsMarco?.ok === false || !modProcesar
                  ? "none"
                  : "0 4px 14px rgba(124,58,237,0.35)",
              }}
            >
              {procesandoRetoque
                ? progresoRetoque?.fase === "archivar"
                  ? `Marco: archivando en Referencias — ${progresoRetoque.captacionActual} (${progresoRetoque.captacionIndex}/${progresoRetoque.captacionesTotal})`
                  : progresoRetoque?.fase?.startsWith("video_horizontal")
                  ? `Marco: generando vídeo horizontal — ${progresoRetoque.captacionActual} (${progresoRetoque.captacionIndex}/${progresoRetoque.captacionesTotal})`
                  : progresoRetoque?.fase?.startsWith("video") || progresoRetoque?.fase === "video"
                  ? progresoRetoque.videoClipActual
                    ? `Marco: vídeo ${progresoRetoque.captacionActual} — clip ${(progresoRetoque.videoClipIndex ?? 0) + 1}/${progresoRetoque.videoClipsTotal || "?"} (${progresoRetoque.captacionIndex}/${progresoRetoque.captacionesTotal})`
                    : `Marco: generando vídeo vertical — ${progresoRetoque.captacionActual} (${progresoRetoque.captacionIndex}/${progresoRetoque.captacionesTotal})`
                  : progresoRetoque?.fotoActual
                    ? `Marco: ${progresoRetoque.captacionActual} — foto ${progresoRetoque.fotoIndex}/${progresoRetoque.fotosTotal} (${progresoRetoque.captacionIndex}/${progresoRetoque.captacionesTotal})`
                    : progresoRetoque?.captacionActual
                      ? `Marco: ${progresoRetoque.captacionActual} (${progresoRetoque.captacionIndex}/${progresoRetoque.captacionesTotal})`
                      : "Marco iniciando…"
                : `Ejecutar Marco (${captacionesSeleccionadas.length} captación${captacionesSeleccionadas.length !== 1 ? "es" : ""})`}
            </button>
            {procesandoRetoque && (
              <p style={{ margin: "10px 0 0 0", fontSize: 12, color: c.textMuted, lineHeight: 1.5 }}>
                Por foto: CLIP interior/exterior → recorte 16:9 → Autoenhance (preset Interior o exterior) → <strong style={{ color: c.textPrimary }}>Web</strong>.
                Después, slideshow → <strong style={{ color: c.textPrimary }}>Web/Video horizontal.mp4</strong>
                {" "}y clips de vídeo → <strong style={{ color: c.textPrimary }}>Web/Video vertical.mp4</strong>.
                Si todo sale bien, borra <strong style={{ color: c.textPrimary }}>Editar</strong> y mueve la captación a <strong style={{ color: c.textPrimary }}>Referencias</strong>.
                El trabajo corre en el servidor{jobIdRetoque ? ` (ID ${jobIdRetoque.slice(0, 8)}…)` : ""}; puedes cerrar esta página y volver más tarde.
              </p>
            )}
          </div>

          {errorRetoque && (
            <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", fontSize: 13, color: "#f87171", fontWeight: 600 }}>
              {errorRetoque}
            </div>
          )}

          {resultadoRetoque && (
            <div style={{ marginTop: 14, padding: "14px 16px", borderRadius: 10, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", fontSize: 13, color: c.textPrimary }}>
              <div style={{ fontWeight: 700, color: "#4ade80", marginBottom: 10 }}>
                {resultadoRetoque.fallos?.length ? "Proceso terminado con avisos" : "Proceso completado"}
              </div>
              <div style={{ marginBottom: 8, color: c.textMuted }}>
                Fotos editadas y subidas a <strong style={{ color: c.textPrimary }}>Web</strong>:{" "}
                <strong style={{ color: c.textPrimary }}>{resultadoRetoque.fotosOk}</strong>
                {resultadoRetoque.resumen?.fotosError > 0 && (
                  <>
                    {" · "}
                    <strong style={{ color: "#f87171" }}>{resultadoRetoque.resumen.fotosError} error(es)</strong>
                  </>
                )}
                {resultadoRetoque.resumen?.videosOk > 0 && (
                  <>
                    {" · "}
                    Vídeos verticales: <strong style={{ color: c.textPrimary }}>{resultadoRetoque.resumen.videosOk}</strong>
                  </>
                )}
                {resultadoRetoque.resumen?.archivadasOk > 0 && (
                  <>
                    {" · "}
                    Archivadas en Referencias: <strong style={{ color: c.textPrimary }}>{resultadoRetoque.resumen.archivadasOk}</strong>
                  </>
                )}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.65, color: c.textMuted }}>
                {resultadoRetoque.resultados.map((r) => (
                  <li key={r.captacion}>
                    <strong style={{ color: c.textPrimary }}>{r.captacion}</strong>
                    {" — "}
                    {r.ok && !r.errores
                      ? `${r.procesadas} foto(s) OK${r.omitidas ? ` (${r.omitidas} ya en Web)` : ""}`
                      : r.mensaje || `${r.procesadas || 0} OK, ${r.errores || 0} error(es)`}
                    {r.videoHorizontal?.ok && !r.videoHorizontal?.omitido && (
                      <>
                        {" · "}
                        <span style={{ color: "#4ade80" }}>
                          Video horizontal ({r.videoHorizontal.fotos} fotos, {r.videoHorizontal.durationSec}s, {r.videoHorizontal.musica})
                        </span>
                      </>
                    )}
                    {r.videoHorizontal?.omitido && (
                      <>
                        {" · "}
                        <span style={{ color: c.textMuted }}>{r.videoHorizontal.mensaje || "Vídeo horizontal omitido"}</span>
                      </>
                    )}
                    {r.videoHorizontal && !r.videoHorizontal.ok && (
                      <>
                        {" · "}
                        <span style={{ color: "#f87171" }}>Vídeo horizontal: {r.videoHorizontal.error}</span>
                      </>
                    )}
                    {r.video?.ok && !r.video?.omitido && (
                      <>
                        {" · "}
                        <span style={{ color: "#4ade80" }}>
                          Video vertical ({r.video.clips} clips, {r.video.durationSec}s, {r.video.musica})
                        </span>
                      </>
                    )}
                    {r.video?.omitido && (
                      <>
                        {" · "}
                        <span style={{ color: c.textMuted }}>{r.video.mensaje || "Sin clips de vídeo"}</span>
                      </>
                    )}
                    {r.video && !r.video.ok && (
                      <>
                        {" · "}
                        <span style={{ color: "#f87171" }}>Vídeo: {r.video.error}</span>
                      </>
                    )}
                    {r.archivo?.ok && (
                      <>
                        {" · "}
                        <span style={{ color: "#4ade80" }}>Movida a Referencias</span>
                      </>
                    )}
                    {r.archivo && !r.archivo.ok && !r.archivo.omitido && (
                      <>
                        {" · "}
                        <span style={{ color: "#f87171" }}>Archivo: {r.archivo.error || r.archivo.motivo}</span>
                      </>
                    )}
                    {r.archivo?.omitido && r.archivo.motivo && !r.archivo.ok && (
                      <>
                        {" · "}
                        <span style={{ color: "#fbbf24" }}>{r.archivo.motivo}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              {resultadoRetoque.omitidas?.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${c.border}` }}>
                  <div style={{ fontWeight: 700, color: "#fbbf24", marginBottom: 6, fontSize: 12 }}>
                    Captaciones no editadas
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55, fontSize: 12, color: c.textMuted }}>
                    {resultadoRetoque.omitidas.map((o) => (
                      <li key={o.captacion}>
                        <strong style={{ color: c.textPrimary }}>{o.captacion}</strong>
                        {o.motivo ? `: ${o.motivo}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resultadoRetoque.fotosFallidas?.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${c.border}` }}>
                  <div style={{ fontWeight: 700, color: "#f87171", marginBottom: 6, fontSize: 12 }}>
                    Fotos con error
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55, fontSize: 12, color: c.textMuted }}>
                    {resultadoRetoque.fotosFallidas.map((f) => (
                      <li key={`${f.captacion}-${f.archivo}`}>
                        <strong style={{ color: c.textPrimary }}>{f.captacion}</strong> / {f.archivo}
                        {f.error ? `: ${f.error}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resultadoRetoque.resultados?.some((r) => r.escenas) && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${c.border}`, fontSize: 12, color: c.textMuted }}>
                  Resumen escenas guardado en <strong style={{ color: c.textPrimary }}>Web/escenas-marco.json</strong> por captación.
                </div>
              )}
            </div>
          )}

          {captacionesSeleccionadas.length > 0 && !procesandoRetoque && !resultadoRetoque && (
            <p style={{ margin: "10px 0 0 0", fontSize: 12, color: c.textMuted }}>
              Se procesarán: {captacionesSeleccionadas.join(" · ")}
            </p>
          )}
        </div>
        )}

        <div style={{ height: 1, background: c.border, margin: "8px 0 28px 0" }} />

        {/* ── Vídeo (contenido anterior) ── */}
        <h2 style={{ margin: "0 0 16px 0", fontSize: 16, fontWeight: 700, color: c.textPrimary }}>
          Vídeo promocional
        </h2>

        <div style={{
          marginBottom: 22,
          padding: "16px 18px",
          borderRadius: 14,
          border: `1px solid ${c.border}`,
          background: isDark ? "#121826" : "#f0f4ff",
          fontSize: 14,
          color: c.textPrimary,
          lineHeight: 1.65,
        }}>
          <p style={{ margin: "0 0 10px 0", fontWeight: 700 }}>Dónde está esta pantalla</p>
          <p style={{ margin: 0, color: c.textMuted, fontWeight: 500 }}>
            Menú <strong style={{ color: c.textPrimary }}>☰</strong> → <strong style={{ color: c.textPrimary }}>«Marco»</strong>
            {" "}o Configuración → Marco. Ruta:{" "}
            <code style={{ fontSize: 12.5, background: isDark ? "#1e293b" : "#e2e8f0", padding: "2px 6px", borderRadius: 6 }}>/configuracion/marco</code>
          </p>
          <p style={{ margin: "14px 0 8px 0", fontWeight: 700, color: c.textPrimary }}>Sobre el vídeo de prueba (Ref. 3494-C)</p>
          <p style={{ margin: 0, color: c.textMuted, fontWeight: 500 }}>
            <strong style={{ color: c.textPrimary }}>«video-referencia-marco» no lo ha hecho Marco:</strong> es el mismo archivo que exportasteis con NodalView (Remotion), copiado al front solo para comparar en pantalla.
          </p>
          <p style={{ margin: "10px 0 0 0", color: c.textMuted, fontWeight: 500 }}>
            Para generar vídeos nuevos, Marco lee las fotos del NAS en <code style={{ fontSize: 12.5 }}>…/web/</code> dentro de cada referencia.
          </p>
        </div>

        <div style={{ marginBottom: 22, background: c.bgCard, borderRadius: 14, border: `1px solid ${c.border}`, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${c.border}`, fontWeight: 700, fontSize: 14, color: c.textPrimary }}>
            Vídeo de referencia (comparar con el que generes)
          </div>
          {referenciaVideoOk ? (
            <video
              key="ref"
              controls
              playsInline
              preload="metadata"
              style={{ width: "100%", display: "block", maxHeight: "min(50vh, 420px)", background: "#000" }}
              src={refVideoSrc}
              onError={onReferenciaVideoError}
            >
              Tu navegador no reproduce vídeo HTML5.
            </video>
          ) : (
            <div style={{ padding: "20px 16px", fontSize: 13, color: c.textMuted, lineHeight: 1.6 }}>
              No está <code style={{ fontSize: 12.5 }}>frontend/public/video-referencia-marco.mp4</code>.
            </div>
          )}
        </div>

        <p style={{ margin: "0 0 14px 0", fontSize: 14, color: c.textMuted, fontWeight: 500, lineHeight: 1.55 }}>
          <strong style={{ color: c.textPrimary }}>Solo referencia (y título si hace falta):</strong> las fotos deben estar en{" "}
          <code style={{ fontSize: 12.5, background: isDark ? "#1e293b" : "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>Ref. [ref] - [título]/web/</code>
        </p>

        <div style={{ background: c.bgCard, borderRadius: 14, border: `1px solid ${c.border}`, overflow: "hidden", boxShadow: isDark ? "0 4px 24px rgba(0,0,0,.3)" : "0 4px 24px rgba(0,0,0,.06)" }}>
          <div style={{ height: 3, background: `linear-gradient(90deg,${accent.from},${accent.to},#c084fc)` }} />
          <div style={{ padding: "24px" }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", marginBottom: 6 }}>Referencia</label>
            <input
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="ej. 3494-C"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${c.border}`, background: isDark ? "#151a24" : "#fff", color: c.textPrimary, fontSize: 15, marginBottom: 16, fontFamily: "inherit", boxSizing: "border-box" }}
            />

            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", marginBottom: 6 }}>Título de la carpeta (opcional)</label>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Si hay varias carpetas con la misma ref., copia el título exacto del NAS"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${c.border}`, background: isDark ? "#151a24" : "#fff", color: c.textPrimary, fontSize: 14, marginBottom: 12, fontFamily: "inherit", boxSizing: "border-box" }}
            />

            <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={buscarCarpetas}
                disabled={cargandoLista}
                style={{ padding: "10px 18px", borderRadius: 10, border: `1px solid ${accent.border}`, background: accent.bg, color: accent.text, fontWeight: 600, fontSize: 13, cursor: cargandoLista ? "wait" : "pointer", fontFamily: "inherit" }}
              >
                {cargandoLista ? "Buscando…" : "Buscar carpetas en el NAS"}
              </button>
            </div>

            {carpetas.length > 0 && (
              <div style={{ marginBottom: 18, padding: "12px 14px", borderRadius: 10, background: isDark ? "#1a2230" : "#f8fafc", border: `1px solid ${c.border}`, fontSize: 12.5, color: c.textMuted }}>
                <div style={{ fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>Coincidencias ({carpetas.length})</div>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                  {carpetas.map((name) => (
                    <li key={name}>
                      <button
                        type="button"
                        onClick={() => {
                          const m = name.match(/^Ref\.\s*.+?\s-\s(.+)$/);
                          if (m) setTitulo(m[1].trim());
                        }}
                        style={{ background: "none", border: "none", color: accent.text, cursor: "pointer", textAlign: "left", fontFamily: "inherit", fontSize: 12.5, padding: 0, textDecoration: "underline" }}
                      >
                        {name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={generarVideo}
              disabled={generando}
              style={{
                width: "100%",
                padding: "14px 20px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg,${accent.from},${accent.to})`,
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: generando ? "wait" : "pointer",
                fontFamily: "inherit",
                opacity: generando ? 0.85 : 1,
                boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
              }}
            >
              {generando ? "Marco está generando el vídeo (puede tardar varios minutos)…" : "Generar vídeo y obtener URL pública"}
            </button>

            {error && (
              <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 10, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", fontSize: 13, color: "#f87171", fontWeight: 600 }}>
                {error}
              </div>
            )}

            {ultimoExito && (
              <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 10, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", fontSize: 13, color: c.textPrimary }}>
                <div style={{ fontWeight: 700, color: "#4ade80", marginBottom: 10 }}>Listo — enlace para compartir</div>
                <div style={{ marginBottom: 10, wordBreak: "break-all", fontSize: 13, lineHeight: 1.5 }}>
                  <a href={ultimoExito.urlLanding} target="_blank" rel="noopener noreferrer" style={{ color: accent.text, fontWeight: 600 }}>
                    {ultimoExito.urlLanding}
                  </a>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => ultimoExito.urlLanding && window.open(ultimoExito.urlLanding, "_blank", "noopener,noreferrer")}
                    style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    Abrir landing
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!ultimoExito.urlLanding) return;
                      try {
                        await navigator.clipboard.writeText(ultimoExito.urlLanding);
                        setCopiado(true);
                        setTimeout(() => setCopiado(false), 2000);
                      } catch {
                        window.prompt("Copia la URL:", ultimoExito.urlLanding);
                      }
                    }}
                    style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.bgCard, color: c.textPrimary, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    {copiado ? "¡Copiado!" : "Copiar URL"}
                  </button>
                </div>
                <div style={{ color: c.textMuted, marginBottom: 4 }}>Fotos usadas: <strong style={{ color: c.textPrimary }}>{ultimoExito.fotos}</strong></div>
                {ultimoExito.carpeta && (
                  <div style={{ color: c.textMuted, marginBottom: 4 }}>Carpeta NAS: <strong style={{ color: c.textPrimary, wordBreak: "break-all" }}>{ultimoExito.carpeta}</strong></div>
                )}
                {ultimoExito.rutaNas && (
                  <div style={{ color: c.textMuted, wordBreak: "break-all", fontSize: 12 }}>Copia en NAS: <strong style={{ color: c.textPrimary }}>{ultimoExito.rutaNas}</strong></div>
                )}
                {ultimoExito.urlVideo && (
                  <div style={{ color: c.textMuted, wordBreak: "break-all", fontSize: 11, marginTop: 6 }}>MP4 directo (R2): {ultimoExito.urlVideo}</div>
                )}
                {ultimoExito.advertencias && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#fbbf24" }}>⚠ {ultimoExito.advertencias}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
